import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CalendarDays, QrCode, Receipt, FileSignature, Clock, TrendingUp, Users, Briefcase, ClipboardList, FileText, LifeBuoy } from "lucide-react";

function Stat({ label, value, icon: Icon, hue = "light", testid }) {
  const dark = hue === "dark";
  return (
    <div data-testid={testid} className={`card-smart p-5 ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        <Icon className={`w-4 h-4 ${dark ? "text-[#FF7A29]" : "text-[#FF7A29]"}`}/>
      </div>
      <div className="font-display text-4xl">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/events/upcoming").then((r) => setUpcoming(r.data));
  }, []);

  return (
    <div className="p-4 md:p-12" data-testid="dashboard">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Dashboard · {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">Ciao, {user?.full_name?.split(" ")[0]}.</h1>
        <p className="text-[#555] mt-2">Ecco il quadro operativo del tuo team oggi.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Dipendenti" value={stats.employees_total ?? "–"} icon={Users} hue="dark" testid="stat-employees" />
        <Stat label="Commesse attive" value={stats.orders_total ?? "–"} icon={Briefcase} testid="stat-orders" />
        <Stat label="Rapportini" value={stats.reports_total ?? "–"} icon={ClipboardList} testid="stat-reports" />
        <Stat label="Cedolini" value={stats.payslips_total ?? "–"} icon={FileText} testid="stat-payslips" />
        <Stat label="Ticket aperti" value={stats.tickets_open ?? "–"} icon={LifeBuoy} testid="stat-tickets" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <Stat label="Timbrature oggi" value={stats.attendances_today ?? "–"} icon={QrCode} hue="dark" testid="stat-att-today" />
        <Stat label="Totale timbrature" value={stats.attendances_total ?? "–"} icon={TrendingUp} testid="stat-att-total" />
        <Stat label="Eventi in agenda" value={stats.events_total ?? "–"} icon={CalendarDays} testid="stat-events" />
        <Stat label="Rimborsi in attesa" value={stats.expenses_pending ?? "–"} icon={Receipt} testid="stat-exp-pending" />
        <Stat label="Documenti" value={stats.documents_total ?? "–"} icon={FileSignature} testid="stat-docs" />
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <section className="md:col-span-7 border border-black bg-white p-8" data-testid="upcoming-events">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl">Prossimi appuntamenti (24h)</h2>
            <Clock className="w-5 h-5 text-[#FF7A29]" />
          </div>
          {upcoming.length === 0 ? (
            <div className="text-sm text-[#757575]">Nessun appuntamento nelle prossime 24 ore.</div>
          ) : (
            <ul className="divide-y divide-[#EDE5D8]">
              {upcoming.slice(0, 8).map((e) => (
                <li key={e.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{e.title}</div>
                    <div className="text-xs text-[#757575]">{new Date(e.start).toLocaleString("it-IT")} · {e.location || "—"}</div>
                  </div>
                  <span className="text-xs font-semibold bg-[#FF7A29] text-white px-2 py-1">{e.minutes_until < 60 ? `${e.minutes_until}m` : `${Math.floor(e.minutes_until / 60)}h`}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="md:col-span-5 border border-black bg-black text-white p-8" data-testid="tip-card">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-4">Suggerimento AI</div>
          <h3 className="font-display text-2xl mb-4">Attiva l'analisi AI delle presenze.</h3>
          <p className="text-sm text-white/70 mb-6">GPT-5.2 analizza le timbrature e produce un report operativo con anomalie e raccomandazioni.</p>
          <a href="/app/ai" className="inline-block bg-[#FF7A29] px-6 py-3 text-sm">Genera report →</a>
        </section>
      </div>
    </div>
  );
}
